import React from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../utils/permissions';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
  requiredRoles?: ('super_admin' | 'finance_admin' | 'town_admin' | 'member_admin')[];
  fallback?: React.ReactNode;
}

export const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({
  children,
  requiredPermissions = [],
  requiredRoles = [],
  fallback = (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
        <p className="text-gray-500">You don't have permission to access this page.</p>
      </div>
    </div>
  )
}) => {
  const { hasAnyPermission, isSuperAdmin, isFinanceAdmin, isTownAdmin } = usePermissions();

  // Check permissions
  const hasRequiredPermissions = requiredPermissions.length === 0 || hasAnyPermission(requiredPermissions);

  // Check roles
  let hasRequiredRole = requiredRoles.length === 0;
  if (!hasRequiredRole) {
    hasRequiredRole = requiredRoles.some(role => {
      switch (role) {
        case 'super_admin':
          return isSuperAdmin();
        case 'finance_admin':
          return isFinanceAdmin();
        case 'town_admin':
          return isTownAdmin();
        case 'member_admin':
          return !isSuperAdmin() && !isFinanceAdmin() && !isTownAdmin();
        default:
          return false;
      }
    });
  }

  const hasAccess = hasRequiredPermissions && hasRequiredRole;

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};